import React, { useState, useEffect } from "react";
import authAxios from "../authAxios";
import { useTranslation } from "react-i18next";
import { useTabletOrMobile } from "../hooks/responsive";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

export const FollowButton = ({ profile }) => {
  const { t } = useTranslation();
  const isMobile = useTabletOrMobile();
  const [following, setFollowing] = useState([]);
  const [isHovered, setIsHovered] = useState(false);

  const followingHash = following.reduce((acc, user) => {
    acc[user.following_id] = {
      is_approved: user.is_approved,
    };
    return acc;
  }, {});

  async function getFollowing() {
    try {
      const { data: following } = await authAxios.get(
        "/v1/social/getFollowing"
      );
      setFollowing(following);
    } catch (error) {
      console.error("Failed to fetch following status:", error);
    }
  }

  useEffect(() => {
    getFollowing();
  }, []);

  const getButtonStyle = () => {
    const defaultStyle =
      "tw-font-manrope tw-font-bold tw-text-sm tw-h-[2rem] tw-flex tw-items-center tw-justify-center tw-w-[7rem] tw-rounded-[6px] tw-text-transform-none";

    if (!followingHash[profile.id]) {
      return `${defaultStyle} tw-bg-blue-500 tw-text-white hover:tw-bg-blue-600`;
    }
    if (!followingHash[profile.id].is_approved) {
      return isHovered
        ? `${defaultStyle} tw-bg-red-500 tw-text-white`
        : `${defaultStyle} tw-bg-gray-200 tw-text-gray-800`;
    }
    return isHovered
      ? `${defaultStyle} tw-bg-red-500 tw-text-white`
      : `${defaultStyle} tw-bg-gray-200 tw-text-gray-800`;
  };

  const getButtonText = () => {
    if (!followingHash[profile.id]) {
      return t("follow", { defaultValue: "Follow" });
    }

    if (!followingHash[profile.id].is_approved) {
      return t("cancel_request", { defaultValue: "Cancel Request" });
    }
    return t("following", { defaultValue: "Following" });
  };

  const handleFollowClick = async () => {
    try {
      if (!followingHash[profile.id]) {
        // Optimistically update UI
        setFollowing((prev) => [
          ...prev,
          {
            following_id: profile.id,
            following_first_name: profile.first_name,
            following_last_name: profile.last_name,
            following_username: profile.username,
            following_image: profile.image,
            is_approved: true,
          },
        ]);
        await authAxios.post(`/v1/social/followUser/${profile.id}`);
      } else if (!followingHash[profile.id].is_approved) {
        // Cancel follow request
        setFollowing((prev) =>
          prev.filter((f) => f.following_id !== profile.id)
        );
        await authAxios.post(`/v1/social/cancelFollowRequest/${profile.id}`);
      } else {
        // Unfollow user
        setFollowing((prev) =>
          prev.filter((f) => f.following_id !== profile.id)
        );
        await authAxios.post(`/v1/social/unfollowUser/${profile.id}`);
      }
    } catch (error) {
      console.error("Failed to update follow status:", error);
      getFollowing(); // Refresh following status on error
    }
  };

  return (
    <button
      className={`${getButtonStyle()}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleFollowClick}
      style={{
        padding: "7px 20px 7px 20px",
        display: "flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      <NotificationsNoneOutlinedIcon
        style={{ fontSize: "18px", marginLeft: "-5px" }}
      />
      {getButtonText()}
    </button>
  );
};
